import { cn } from "@/lib/utils";
import type { LeadFormSkinDefinition } from "./types";

/**
 * Skin 3001 — "IITKGP style form", the Lead Drop 8 look.
 *
 * Typography, spacing, radii and the breakpoint ladder are lifted from
 * `widgets/leaddrop/leaddrop8/Leaddrop8.tsx` — specifically its `figmaIitLeadModalChrome`
 * branch, which is the IIT modal treatment rather than variant 8's older default chrome.
 * The same reasoning as skin 3000 applies: variant 8 and this skin will run on the same
 * site during the migration, and a visitor who meets both must not see two products.
 *
 * ─── What makes this skin different from 3000 ───────────────────────────────────────
 * 3000 is a narrow bordered card that stays narrow in a dialog. 8 is the opposite: the
 * popup is a 908px two-column sheet with a full-height 394×565 hero rail, and the mobile
 * sheet is a 320px full-bleed card with the signature asymmetric corners
 * (`rounded-tr-[42px]` / `rounded-bl-[42px]`). Those corners and that fixed 565px row are
 * the whole identity of the design — flattening them to a rounded rectangle produces
 * something that is merely "a popup", not this one.
 *
 * The embedded surface is NOT that sheet scaled down. Variant 8's `embedded` branch is a
 * transparent, borderless column whose fields cap at 314px and whose CTA sits on a hairline
 * top rule — it is designed to sit inside a host section that already provides the card.
 * Copying the popup's chrome into the page is what produces a card floating inside a card.
 *
 * ─── The odd breakpoint names ───────────────────────────────────────────────────────
 * Breakpoints are written as `min-[768px]:` / `min-[1080px]:` rather than `tab:` / `sm:`.
 * This app redefines those tokens in globals.css (`sm` is 1080px, not 640px) and then
 * partly walks it back in a second @theme block, so the named variants are ambiguous.
 * Spelling them out is deliberate — do not "tidy" them back.
 *
 * Every colour is a CSS variable fed from the CMS settings below, so an author configures
 * this skin the way they already configure lead form variant 8 — same knob names.
 */

/**
 * The field baseline — Leaddrop8's `figmaInput`: borderless, shadowed, 36px tall.
 *
 * Fill and border colour resolve through `--lf-field-fill` / `--lf-field-stroke` so the
 * invalid state can REDECLARE those variables instead of adding a second `bg-*`/`border-*`
 * utility to the same element. Two utilities for one property are resolved by the order
 * Tailwind emits them, not by the order they are composed here — so `fieldInvalid` winning
 * would have been luck rather than design.
 */
const FIELD_BASE = cn(
  "flex h-9 w-full min-w-0 rounded-md border px-[10px] py-[8px]",
  "font-poppins text-[14px] font-normal leading-[138%] shadow-sm",
  "border-[var(--lf-field-stroke,transparent)] bg-[var(--lf-field-fill,var(--lf-field-bg))]",
  "text-[var(--lf-field-text)]",
  "transition-colors placeholder:font-normal placeholder:text-[#848484]",
  "outline-none ring-0 focus:ring-0 focus-visible:outline-none focus-visible:ring-0",
  "disabled:cursor-not-allowed disabled:opacity-50",
  "[-webkit-tap-highlight-color:transparent]",
);

/** The signature asymmetric corners. Shared by the mobile sheet and the hero rail. */
const SHEET_CORNERS = "rounded-tr-[42px] rounded-bl-[42px]";

/** Variant 8's brand colour, straight from LEAD_DROP_POPUP_8_CONSTANTS.DEFAULT_ROW. */
const BRAND = "#DE5803";
const INK = "#1F1F1F";
/** Leaddrop8's error tone — the field turns `#FFF3F4` and the message `#F74958`. */
const DANGER = "#F74958";
/** The thank-you accent, used for the ending's headline. */
const ACCENT = "#00A571";
/** The success tick's disc. Same value; named separately because it means something else. */
const BADGE = "#00A571";

const skin3001: LeadFormSkinDefinition = {
  id: "3001",
  name: "IITKGP style form",
  description:
    "Lead Drop 8's IIT sheet: 908px two-column popup with a full-height hero rail, asymmetric corners, borderless shadowed fields.",
  surfaces: ["fixed", "popup"],
  /** Variant 8 pins the course on a course-bound page — see `bindsCourseToPage` in ./types. */
  bindsCourseToPage: true,
  /** Variant 8's thank-you keeps the host's chrome on both surfaces — see ./types. */
  endingKeepsShellChrome: true,

  /**
   * ─── The CMS an author already knows ──────────────────────────────────────────────
   *
   * These are lead popup 8's OWN column names, labels and defaults, taken from
   * `constants/admin/fields/leadDropPopup/leadDropPopup_8.ts` — `button_color`,
   * `get_otp_button_text`, `phone_row_background_color`, `desktop_bg_image` and the rest.
   * Someone who has configured variant 8 finds the same knobs with the same words here;
   * translating them into this file's canonical vocabulary would have made a familiar form
   * unfamiliar for no gain. `normalizeValues` below bridges them to the names the renderer
   * reads.
   *
   * Two deliberate departures from that admin screen, both forced by what a custom form is:
   *
   *   · Variant 8's per-field label/placeholder/required config (First name, Email,
   *     Qualification, Program) is NOT here. Those are questions, and questions are the
   *     Form Builder's job now — the whole point of a config-driven form is that the
   *     author composes them rather than filling in five fixed slots.
   *   · Variant 8 HIDES the ending copy (`new_user_*`, `existing_user_*`, `brochure_*`)
   *     from its admin and lets it default to blank in the row. A custom form has no row
   *     to inherit from, so blank would mean a thank-you screen with no words on it. They
   *     are exposed here under variant 8's own key names and colour defaults.
   */
  settingGroups: [
    {
      title: "Header",
      settings: [
        {
          key: "form_title",
          label: "Form title",
          type: "text",
          store: "copy",
          defaultValue: "Please Enter Your Phone No.",
          placeholder: "Please Enter Your Phone No.",
        },
        { key: "form_title_color", label: "Form title color", type: "color", store: "theme", defaultValue: BRAND },
        {
          key: "form_sub_text",
          label: "Form sub text",
          type: "textarea",
          store: "copy",
          defaultValue: "",
          help: "Variant 8 ships with this blank — the header is just the title.",
        },
        { key: "form_sub_text_color", label: "Form sub text color", type: "color", store: "theme", defaultValue: INK },
      ],
    },
    {
      title: "Buttons",
      settings: [
        {
          key: "get_otp_button_text",
          label: "Phone-step button label",
          type: "text",
          store: "copy",
          defaultValue: "Get OTP",
          placeholder: "Get OTP",
        },
        {
          key: "verify_otp_button_text",
          label: "OTP-step button label",
          type: "text",
          store: "copy",
          defaultValue: "Verify OTP",
          placeholder: "Verify OTP",
        },
        {
          key: "signup_button_text",
          label: "Submit button label",
          type: "text",
          store: "copy",
          defaultValue: "Continue",
          placeholder: "Continue",
          help: "Shown on the step that submits the lead.",
        },
        { key: "button_color", label: "Button color", type: "color", store: "theme", defaultValue: BRAND },
        { key: "button_text_color", label: "Button text color", type: "color", store: "theme", defaultValue: "#FFFFFF" },
        {
          key: "get_otp_enabled_color",
          label: '"Get OTP" enabled color',
          type: "color",
          store: "theme",
          defaultValue: BRAND,
          help: "Used for the phone step when set; otherwise the button color above applies.",
        },
        {
          key: "get_otp_disabled_color",
          label: '"Get OTP" disabled color',
          type: "color",
          store: "theme",
          defaultValue: "#D4D4D8",
          help: "The button fill while the step cannot yet be submitted.",
        },
        {
          key: "skip_text",
          label: "Skip button label",
          type: "text",
          store: "copy",
          defaultValue: "Skip for now",
          help: "Only appears on steps the Form Builder marks optional.",
        },
      ],
    },
    {
      title: "Fields",
      settings: [
        {
          key: "phone_row_background_color",
          label: "Phone row background",
          type: "color",
          store: "theme",
          defaultValue: "#F0F0F7",
          help: "Variant 8's fields are borderless — this is the fill behind every one of them.",
        },
        {
          key: "label_color",
          label: "Field label colour",
          type: "color",
          store: "theme",
          defaultValue: "#393939",
        },
        {
          key: "field_text_color",
          label: "Field text colour",
          type: "color",
          store: "theme",
          defaultValue: "#464646",
        },
        {
          key: "embedded_background_color",
          label: "Inline card background",
          type: "color",
          store: "theme",
          defaultValue: "#E5E5F9",
          help: "The inline card only. The popup sheet is white, as variant 8 draws it.",
        },
      ],
    },
    {
      title: "OTP step",
      settings: [
        {
          key: "otp_title_text",
          label: "OTP title text",
          type: "text",
          store: "copy",
          defaultValue: "Enter 4-digit OTP Here",
          placeholder: "Enter 4-digit OTP Here",
        },
        { key: "otp_title_color", label: "OTP title color", type: "color", store: "theme", defaultValue: "#000000" },
        { key: "edit_text_color", label: '"Change Number" color', type: "color", store: "theme", defaultValue: BRAND },
        { key: "resend_text_color", label: '"Resend OTP" color', type: "color", store: "theme", defaultValue: BRAND },
      ],
    },
    {
      title: "Consent",
      settings: [
        {
          key: "checkbox_description",
          label: "Checkbox description",
          type: "textarea",
          store: "copy",
          defaultValue: "",
          help: "Leave blank for no consent line. Links below are woven into this text.",
        },
        { key: "checkbox_description_color", label: "Checkbox description color", type: "color", store: "theme", defaultValue: "#666666" },
        { key: "checkbox_color", label: "Checkbox color", type: "color", store: "theme", defaultValue: BRAND },
        { key: "terms_conditions_link", label: "Terms & conditions link", type: "url", store: "theme", defaultValue: "" },
        { key: "privacy_policy_link", label: "Privacy policy link", type: "url", store: "theme", defaultValue: "" },
      ],
    },
    {
      // Every knob here draws the image rail, which only the popup surface has — see
      // `surfaces` on LeadFormSkinSetting. On an inline form they are hidden rather than
      // shown-and-inert, and any value already stored still round-trips on save.
      title: "Popup imagery",
      settings: [
        {
          key: "desktop_bg_image",
          label: "Desktop background image",
          type: "image",
          store: "copy",
          defaultValue: "",
          help: "The 394×565 hero rail beside the fields, shown from 1080px up.",
          surfaces: ["popup"],
        },
        {
          key: "mobile_bg_image",
          label: "Mobile background image",
          type: "image",
          store: "copy",
          defaultValue: "",
          help: "The strip across the top of the mobile sheet, below 1080px. Falls back to the desktop image when left empty.",
          surfaces: ["popup"],
        },
        {
          key: "bg_image_alt",
          label: "Background image alt text",
          type: "text",
          store: "copy",
          defaultValue: "",
          surfaces: ["popup"],
        },
        {
          key: "desktop_image_bg_color",
          label: "Desktop image background color",
          type: "color",
          store: "theme",
          defaultValue: "#CD9986",
          help: "Painted behind the rail, so a transparent image sits on a colour rather than on white.",
          surfaces: ["popup"],
        },
      ],
    },
    {
      title: "Success buttons",
      settings: [
        {
          key: "success_button_text_with_pok",
          label: "Success button text — when a destination is set",
          type: "text",
          store: "copy",
          defaultValue: "Continue with Your Application",
        },
        {
          key: "success_button_text_without_pok",
          label: "Success button text — when there is no destination",
          type: "text",
          store: "copy",
          defaultValue: "Close",
        },
        {
          key: "success_badge_color",
          label: "Success tick colour",
          type: "color",
          store: "theme",
          defaultValue: BADGE,
          help: "The disc behind the tick on every ending. Green by default because it reads as “this worked”.",
        },
        {
          key: "course_text_color",
          label: "Course name colour",
          type: "color",
          store: "theme",
          defaultValue: ACCENT,
          help: "Colour of {course} where it appears in the ending copy — e.g. “Thank you for your interest in {course}”.",
        },
        {
          key: "enable_application_redirect",
          label: "Send the button to the course's application page",
          type: "toggle",
          store: "copy",
          defaultValue: false,
          help: "Uses the POK of the course the visitor picked — variant 8's post-submit behaviour. Falls back to the link below when the course has no POK.",
        },
        {
          key: "application_redirect_path",
          label: "Application path",
          type: "text",
          store: "copy",
          defaultValue: "/application/{slug}",
          placeholder: "/application/{slug}",
          help: "{slug} is replaced with the selected course's POK — /application/{slug} becomes /application/POK1.",
        },
        {
          key: "success_redirect_seconds",
          label: "Auto-redirect after (seconds)",
          type: "text",
          store: "copy",
          defaultValue: "7",
          help: "Counts down under the button and then follows it automatically. 0 or blank keeps the button manual. Only runs when there is somewhere to go.",
        },
        {
          key: "success_button_link",
          label: "Success button link (optional)",
          type: "url",
          store: "copy",
          defaultValue: "",
          help: "Opens in a new tab when set. Left empty the button closes the popup.",
        },
      ],
    },
    {
      title: "Ending — new user",
      settings: [
        {
          key: "new_user_title", label: "New user title", type: "text", store: "copy", defaultValue: "Thank you for registering!",
          help: "Use {course} to insert the programme the visitor chose — it takes the “Course name colour”.",
        },
        {
          key: "new_user_description", label: "New user description", type: "textarea", store: "copy", defaultValue: "Our admissions team will call you shortly.",
          help: "Use {course} for the programme chosen. {{Download}} becomes the manual download link.",
        },
        { key: "new_user_title_color", label: "New user title color", type: "color", store: "theme", defaultValue: "#000000" },
        { key: "new_user_description_color", label: "New user description color", type: "color", store: "theme", defaultValue: "#666666" },
      ],
    },
    {
      title: "Ending — returning user",
      settings: [
        {
          key: "existing_user_title", label: "Existing user title", type: "text", store: "copy", defaultValue: "Welcome back!",
          help: "Use {course} to insert the programme the visitor chose — it takes the “Course name colour”.",
        },
        {
          key: "existing_user_description", label: "Existing user description", type: "textarea", store: "copy", defaultValue: "You have already registered with us. Our team will be in touch.",
          help: "Use {course} for the programme chosen. {{Download}} becomes the manual download link.",
        },
        { key: "existing_user_title_color", label: "Existing user title color", type: "color", store: "theme", defaultValue: "#000000" },
        { key: "existing_user_description_color", label: "Existing user description color", type: "color", store: "theme", defaultValue: "#666666" },
      ],
    },
    {
      title: "Ending — brochure",
      settings: [
        {
          key: "brochure_title", label: "Brochure title", type: "text", store: "copy", defaultValue: "Your brochure is on its way",
          help: "Use {course} to insert the programme the visitor chose — it takes the “Course name colour”.",
        },
        {
          key: "brochure_description", label: "Brochure description", type: "textarea", store: "copy", defaultValue: "The download starts automatically. {{link}} if it does not.",
          help: "Use {course} for the programme chosen. {{Download}} becomes the manual download link.",
        },
        { key: "brochure_title_color", label: "Brochure title color", type: "color", store: "theme", defaultValue: "#000000" },
        { key: "brochure_description_color", label: "Brochure description color", type: "color", store: "theme", defaultValue: "#666666" },
        { key: "brochure_link_color", label: "Brochure link color", type: "color", store: "theme", defaultValue: BRAND },
      ],
    },
  ],

  /**
   * Variant 8's column names → the canonical keys `ConfigLeadForm` reads.
   *
   * Additive: the author's own keys stay untouched, so the settings editor above keeps
   * reading and writing exactly what it declared. See `normalizeValues` in ./types.
   */
  normalizeValues: (v) => {
    const pick = (...vals: (string | undefined)[]) =>
      vals.find((x) => typeof x === "string" && x.trim() !== "");
    /**
     * Variant 8 labels the ending CTA by whether the course has a POK — "continue to your
     * application" vs "close". The renderer has no POK to test, but it has the equivalent
     * fork: a destination link is set, or the button just closes. That is the same question
     * in the terms this form can actually answer.
     */
    const endingCta = String(v.success_button_link ?? "").trim()
      ? v.success_button_text_with_pok
      : v.success_button_text_without_pok;

    return {
      ...v,
      /**
       * Wording variant 8 HARDCODES.
       *
       * Its OTP screen offers colour knobs (`otp_title_color`, `edit_text_color`,
       * `resend_text_color`) but no text ones — the sentences live in `OtpScreen.tsx`. The
       * renderer reads them as settings, so without these four the gate would render as a
       * bare row of boxes with no explanation and an unlabelled link. Supplying them here
       * rather than as settings is what keeps the CMS matching variant 8's.
       */
      otp_description_text: "OTP sent to",
      edit_number_text: "Edit",
      resend_prefix_text: "",
      resend_otp_text: "Resend OTP",
      /** Variant 8 shows no "Step 2 of 3" counter. */
      show_step_indicator: false,
      /* Buttons */
      button_background_color: pick(v.button_color, v.get_otp_enabled_color),
      button_step_1_text: v.get_otp_button_text,
      button_step_2_text: v.verify_otp_button_text,
      button_step_2_background_color: pick(v.button_color, v.get_otp_enabled_color),
      button_step_2_text_color: v.button_text_color,
      submit_text: v.signup_button_text,
      /* OTP gate */
      otp_header_text: v.otp_title_text,
      otp_header_text_color: v.otp_title_color,
      edit_number_text_color: v.edit_text_color,
      resend_otp_text_color: v.resend_text_color,
      /* Popup imagery. Mapped straight through, NOT falling back to the desktop file:
         the renderer needs to see that the author left this empty so it can skip the
         `<source>` and serve one image everywhere. Substituting the desktop URL here made
         the two look distinct and pinned the tall rail crop into the 134px phone strip. */
      popup_desktop_image: v.desktop_bg_image,
      popup_mobile_image: v.mobile_bg_image,
      popup_image_alt: v.bg_image_alt,
      /* Endings */
      download_title: v.brochure_title,
      download_description: v.brochure_description,
      download_title_color: v.brochure_title_color,
      download_description_color: v.brochure_description_color,
      new_user_button_text: endingCta,
      existing_user_button_text: endingCta,
      // Variant 8's brochure screen IS the thank-you screen, button and all.
      download_button_text: endingCta,
      download_button_text_color: pick(v.button_color, v.get_otp_enabled_color),
      new_user_button_text_color: pick(v.button_color, v.get_otp_enabled_color),
      existing_user_button_text_color: pick(v.button_color, v.get_otp_enabled_color),
    };
  },

  /**
   * The success badge — an 80px green disc with a white tick.
   *
   * This is `renderBackdrop` used for something in FLOW rather than behind: the hook runs
   * as the first child of the terminal card, and this skin's card is a centred column, so
   * the badge simply lands above the words. Variant 8 draws it for all three outcomes, so
   * there is nothing to branch on.
   *
   * Defaults to the green `LeadDrop8ThankYou` hardcodes — `#00a571` reads as "this worked",
   * and a university's accent would turn a success signal into decoration. It is a SETTING
   * rather than a constant because a university whose brand IS green needs to move it, and
   * a hardcoded tick is the one thing they cannot work around.
   */
  renderBackdrop: (v) => (
    <div
      className={cn(
        "relative z-[2] flex size-20 shrink-0 items-center justify-center rounded-full",
        "shadow-[0_8px_24px_rgba(0,0,0,0.15)]",
        /**
         * Below 1080px the badge straddles the seam between the hero strip and the white
         * column, exactly as `LeadDrop8ThankYou` positions it (absolute, at 218.5/307 of the
         * strip's height, translated -50%). Cancelled at 1080+, where the image becomes a
         * full-height rail BESIDE the column and there is no seam to straddle.
         *
         * This only works because the container is `justify-start`. Under `justify-center`
         * the free space is re-distributed and the pull is handed straight back.
         *
         * Done with a negative margin rather than absolute positioning because the badge is
         * rendered inside the content column, not inside the strip — the class contract has
         * no hook into the shell's aside. 88px is the offset it has to cancel: the column's
         * `pt-8` (32) plus the content's `py-4` (16) plus half the badge (40).
         *
         * ONLY when a hero image exists. With no strip there is no seam, and pulling the
         * badge up 88px would lift it clean off the top of the card.
         */
        (v.popup_desktop_image || v.popup_mobile_image) && "-mt-[88px] min-[1080px]:mt-0",
      )}
      style={{ backgroundColor: v.success_badge_color || BADGE }}
      aria-hidden
    >
      <svg width="44" height="44" viewBox="0 0 24 24" fill="none">
        <path
          d="M4.5 12.5l5 5 10-11"
          stroke="#FFFFFF"
          strokeWidth="2.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  ),

  cssVars: (v) => {
    // Variant 8's phone step and its other steps carry the same fill by default; the
    // enabled-colour knob exists so an author CAN split them, and loses to an explicit
    // button colour when both are set.
    const primary = v.button_color || v.get_otp_enabled_color || BRAND;
    const onPrimary = v.button_text_color || "#FFFFFF";
    const fieldFill = v.phone_row_background_color || "#F0F0F7";
    return {
      "--lf-title": v.form_title_color || BRAND,
      "--lf-subtitle": v.form_sub_text_color || INK,
      // The popup sheet is white and borderless in variant 8, and not author-editable
      // there. Only the inline card's fill is offered, because that one genuinely differs.
      "--lf-card-bg": v.card_background_color || "#FFFFFF",
      "--lf-card-border": v.card_border_color || "transparent",
      "--lf-embed-bg": v.embedded_background_color || "#E5E5F9",
      "--lf-aside-bg": v.desktop_image_bg_color || "#CD9986",
      "--lf-label": v.label_color || "#393939",
      "--lf-field-bg": fieldFill,
      "--lf-field-border": fieldFill,
      "--lf-field-text": v.field_text_color || "#464646",
      "--lf-primary": primary,
      "--lf-on-primary": onPrimary,
      "--lf-primary-disabled": v.get_otp_disabled_color || "#D4D4D8",
      // The renderer paints the OTP step's CTA from these; variant 8 uses one colour for
      // both steps, so they simply follow the primary.
      "--lf-primary-2": primary,
      "--lf-on-primary-2": onPrimary,
      "--lf-edit-number": v.edit_text_color || BRAND,
      "--lf-resend": v.resend_text_color || BRAND,
      "--lf-otp-desc": v.otp_description_text_color || "#888888",
      "--lf-otp-header": v.otp_title_color || "#000000",
      "--lf-consent": v.checkbox_description_color || "#666666",
      "--lf-consent-link": v.checkbox_color || BRAND,
      "--lf-danger": DANGER,
      "--lf-success-badge": v.success_badge_color || BADGE,
    } as React.CSSProperties;
  },

  classes: {
    /* ── Embedded ────────────────────────────────────────────────────────────────
     * A card in its own right, NOT the popup sheet at a smaller width. `LeaddropForm8`
     * draws a lavender 392px card with the design's asymmetric corners and its own
     * generous padding; the popup is a white 908px sheet. Deriving one from the other
     * gives either a stretched inline card or a popup that looks like a page section. */
    card: cn(
      "relative flex min-w-0 shrink-0 flex-col items-start overflow-hidden",
      // Lifted from `widgets/leaddropform/LeaddropForm8.tsx`: the lavender card with the
      // design's asymmetric corners, floored at 500px so the shorter OTP screen does not
      // shrink it and shift the page around it, capped at the 392px design width.
      "bg-[var(--lf-embed-bg)]",
      SHEET_CORNERS,
      "px-[39px] pb-[26px] pt-[40px]",
      "min-h-[500px]",
      /**
       * `w-[392px] max-w-full`, NOT `w-full max-w-[392px]`.
       *
       * The two look equivalent and are not. `w-full` is `width: 100%`, which needs a
       * parent with a DEFINITE width to mean anything — and the host column does not
       * have one: `Enrollments5` gives it `min-[1080px]:w-fit`. Under a shrink-to-fit
       * parent, 100% resolves to the card's own max-content, so the card ends up as wide
       * as whatever that step happens to contain. The phone step's 28px heading pushed it
       * to the 392px cap; the OTP step, whose widest row is four 44px boxes, did not —
       * so the card visibly narrowed at the gate and the page reflowed around it.
       *
       * A declared width is definite whatever the parent is, so `w-fit` now measures 392
       * on every step. `max-w-full` still lets a narrower column (`min-[1024px]:w-[360px]`)
       * and a phone viewport squeeze it, which is what the old `w-full` was there for.
       *
       * The admin preview never showed this: its stage is a plain `max-w-[520px]` block,
       * i.e. definite, so `w-full` behaved.
       */
      "w-[392px] max-w-full",
      /**
       * The type scale for a 392px card, published as variables the shared `title` and
       * `successButton` classes read.
       *
       * This is a per-COMPOSITION difference, not a per-skin one: the same heading has
       * 314px of room here and 540px in the popup card, so one font size cannot serve
       * both. Variables rather than a second `text-*` utility on the element, because two
       * utilities for one property resolve by Tailwind's emission order — see
       * `.claude/rules/tailwind-conflicts.md`.
       *
       * 21px is measured, not chosen: "Get a Free Career Counselling" is 401px at 28px
       * and 301px at 21px, against 314px of content box. 22px comes to 315px and wraps.
       */
      "[--lf-title-size:18px] min-[768px]:[--lf-title-size:21px]",
      "min-[768px]:[--lf-title-leading:1.28]",
      // Ending CTA: "Continue with Your Application" is 249px at 16px and 233px at 15px,
      // against 256px of usable button once the icon, gap and padding are taken out.
      "[--lf-ending-cta-size:15px]",
      /**
       * Variant 8 renders NO inline form below 768px — the host collapses it to a CTA that
       * opens the popup instead. A skin cannot do that (it has no dialog to open), so this
       * narrows the padding rather than disappearing: a page that placed an inline form
       * must still show one on a phone.
       *
       * The height FLOOR is deliberately not cancelled here. It used to carry
       * `max-[767px]:min-h-0`, and that is what made the card resize on every step — 444px
       * on the first screen, 350 at the gate, 295 on the ending — so the page jumped under
       * the visitor each time they advanced. The floor exists for exactly that reason (see
       * the note above it), and it is no less true on a phone, where the shift is a larger
       * share of the screen.
       */
      "max-[767px]:px-6 max-[767px]:pb-6 max-[767px]:pt-8",
    ),
    cardInner: "flex h-full min-h-0 w-full min-w-0 flex-1 flex-col gap-[26px]",

    /* ── Popup ───────────────────────────────────────────────────────────────────
     * Three distinct compositions, exactly as variant 8 cuts them:
     *   <768px     → a 320px full-bleed sheet, asymmetric corners, hero strip on top
     *   768–1079px → a plain rounded 620px card, no hero at all
     *   1080px+    → the 908px artboard: a 394×565 hero rail beside a 420px form column
     *
     * Written as arbitrary `min-[…]` widths rather than `tab:`/`sm:` because this app
     * redefines those tokens (`sm` is 1080px, not 640px) and then partly walks it back in a
     * second @theme block — see globals.css. skin3000 spells its breakpoints out for the
     * same reason. Do not "tidy" these back to named variants.
     *
     * `max-h`/`overflow-y-auto` are additions, not from the widget: the widget can assume
     * its own fixed content height, whereas a CMS author can add steps until the sheet is
     * taller than the viewport, and a popup that cannot scroll is a popup that traps. */
    popupShell: cn(
      // `shrink-0` is load-bearing: the shell is a flex CHILD of the dialog body and of the
      // admin preview stage, so without it the declared 620/908px widths are treated as a
      // starting size and squeezed down to whatever the container has — the desktop
      // composition then never renders at all, at any viewport.
      "relative flex w-full shrink-0 flex-col bg-[var(--lf-card-bg)]",
      "border border-[var(--lf-card-border)]",
      "transition-[width,max-width,border-radius,padding] duration-300 ease-in-out",
      // `overflow-x-hidden overflow-y-auto` rather than `overflow-hidden` + `overflow-y-auto`.
      // Those two set the SAME axis via a shorthand and a longhand, so which one wins is
      // decided by the order Tailwind emits them, not by the order they are written here.
      "overflow-x-hidden overflow-y-auto",
      // <768: the full-bleed mobile sheet with the signature corners. No padding — the hero
      // strip runs edge to edge, so the inset lives on `popupColumn` instead.
      "mx-auto w-[min(320px,calc(100vw-2rem))] max-w-full p-0",
      SHEET_CORNERS,
      "min-h-[min(600px,90dvh)] max-h-[90dvh]",
      // 768–1079: a plain rounded 620px card, no hero at any width.
      "min-[768px]:w-[620px] min-[768px]:max-w-[620px] min-[768px]:min-h-0 min-[768px]:rounded-xl min-[768px]:px-[29px] min-[768px]:py-[30px]",
      "min-[768px]:max-h-[calc(100dvh-2rem)]",
      /**
       * The popup's type scale — the counterpart of the block on `card`. Variant 8's own
       * 28px/36px heading survives here because this column is 540px wide (420 beside the
       * rail), where the inline card has 314px.
       */
      "[--lf-title-size:18px] min-[768px]:[--lf-title-size:28px]",
      "min-[768px]:[--lf-title-leading:1.28]",
      // The 320px sheet is the tightest ending of all — 280px of column, so the CTA's
      // label has 222px once the icon, gap and padding are removed. 14px measures 218px.
      "max-[767px]:[--lf-ending-cta-size:14px]",
    ),
    /**
     * Applied on top of `popupShell` only when a hero is configured — this is what opens
     * the sheet up to the 908px two-column artboard. Without a hero the shell stays the
     * 620px card, which is the right answer for a single column of fields.
     */
    popupShellWithAside: "min-[1080px]:w-[908px] min-[1080px]:max-w-[908px]",
    popupInner: cn(
      "flex min-h-0 w-full min-w-0 flex-1 flex-col gap-0",
      // The fixed 565px row IS the artboard, and it only exists at 1080+ where the rail
      // sits beside the fields. Below that the sheet is a column and must size to content.
      "min-[1080px]:h-[565px] min-[1080px]:min-h-[565px] min-[1080px]:max-h-[565px] min-[1080px]:flex-row min-[1080px]:items-start min-[1080px]:gap-8",
    ),
    popupAside: cn(
      // <768: the 134px strip across the top of the sheet, carrying the top-right corner.
      "relative w-full shrink-0 overflow-hidden",
      // 134px, variant 8's strip height. Letting the strip take the artwork's own height
      // instead was tried and reverted: the SAME element sits above the fields on every
      // step, so a taller strip pushed the whole form down the phone sheet.
      "h-[134px] rounded-tr-[42px]",
      // The fill is DESKTOP ONLY — the knob is called `desktop_image_bg_color`, and it
      // exists so a transparent rail PNG lands on variant 8's colour instead of on white.
      // Painting it at every width put a peach band across the phone sheet: mobile
      // artwork is typically a shape on a transparent ground (an arc, a wave), and the
      // whole area under that shape lit up in the rail's colour. Below 1080 the strip
      // must read as part of the white sheet, so the transparency stays transparent.
      // Base + responsive variant of ONE property, not two competing utilities.
      "bg-transparent min-[1080px]:bg-[var(--lf-aside-bg)]",
      // 768–1079: the strip STAYS. Variant 8 shows nothing in this band, which leaves the
      // tablet popup with no imagery at all — a plain box that reads as a broken variant
      // rather than a deliberate one. It keeps the strip and grows it slightly instead.
      "min-[768px]:h-[160px]",
      // 1080+: the full-height rail, with the design's asymmetric left corners.
      "min-[1080px]:h-[565px] min-[1080px]:w-[394px]",
      "min-[1080px]:rounded-tr-none min-[1080px]:rounded-tl-[47px] min-[1080px]:rounded-bl-[45px]",
    ),
    /**
     * The strip is wider than most mobile artwork is shaped, so `object-cover` always
     * crops some height — the question is which end survives.
     *
     * TOP below 1080. Mobile artwork is a header shape: a flat band with an arc or wave
     * cut out of its bottom. Anchoring to the bottom kept the arc and threw away the flat
     * band above it, so the curve sat jammed against the sheet's top edge with the
     * design's breathing room gone.
     *
     * BOTTOM from 1080 up, where this is the 394×565 rail and variant 8 anchors its
     * photograph to the bottom.
     */
    popupAsideImage: "h-full w-full object-cover object-top min-[1080px]:object-bottom",
    popupColumn: cn(
      // The header and the fields are separate blocks and need air between them — variant 8
      // spaces its popup column the same way. Without a gap here the title sat directly on
      // the first label, which the inline card never showed because `cardInner` has one.
      "flex min-h-0 min-w-0 flex-1 flex-col justify-start gap-5 min-[768px]:gap-6",
      // The mobile sheet is full-bleed, so the inset lives here rather than on the shell —
      // on the shell it would inset the hero strip too and break the edge-to-edge crop.
      // The sheet's own padding stops at the shell; the column still needs to start below
      // the close button rather than level with it, which is what made the desktop popup
      // look like the form began at the very top edge.
      "px-5 pb-8 pt-8 min-[768px]:px-0 min-[768px]:pb-0 min-[768px]:pt-6 min-[1080px]:pt-10",
      // Variant 8's own column ladder: 420 on the sheet, 540 in the 620px card, 420 again
      // beside the rail.
      "mx-auto w-full max-w-[420px] min-[768px]:max-w-[540px] min-[1080px]:max-w-[420px]",
      "min-[1080px]:h-[565px] min-[1080px]:self-stretch",
    ),
    popupClose: cn(
      "absolute right-3 top-3 z-[60] flex size-10 cursor-pointer items-center justify-center",
      "min-[768px]:right-4 min-[768px]:top-4",
      "transition-opacity hover:opacity-70 active:opacity-70",
      "outline-none ring-0 focus:outline-none focus:ring-0 focus-visible:outline-none",
      "[-webkit-tap-highlight-color:transparent]",
    ),

    /* ── Shared content ──────────────────────────────────────────────────────────
     * Left-aligned, unlike skin 3000's centred header — variant 8's popup header is
     * `text-left` at 28px/36px, and centring it is the single most obvious tell that a
     * form is not this design. */
    header: "flex w-full flex-col gap-1",
    /**
     * The phone sheet sets its heading ON the arc, in white — the live IITKGP popup does
     * exactly this, and it is why the strip can be tall without costing the form any
     * room: the header occupies the image instead of the space under it.
     *
     * Every utility is `max-[767px]:` scoped rather than base + a `min-[768px]:` reset.
     * A reset would need a second utility for each property this sets — the pattern that
     * makes which one wins depend on Tailwind's emission order — and there is no sane
     * "undo" for a custom property once declared. Above 768 this class contributes
     * nothing at all and the header is simply in flow.
     *
     * `[&>p]` rather than `text-center` on the wrapper: `title` and `subtitle` set
     * `text-left` on themselves, and an inherited alignment loses to a declared one.
     */
    headerOnAside: cn(
      "max-[767px]:absolute max-[767px]:inset-x-0 max-[767px]:top-0 max-[767px]:z-[2]",
      // Matches `popupAside`'s own height so the heading centres on the strip.
      "max-[767px]:h-[134px] max-[767px]:items-center max-[767px]:justify-center",
      "max-[767px]:px-6",
      "max-[767px]:[&>p]:text-center",
      // Custom properties inherit, so redeclaring them here recolours the title and
      // subtitle without either of them needing a second `text-*` utility.
      "max-[767px]:[--lf-title:#F8F9FA] max-[767px]:[--lf-subtitle:#F8F9FA]",
      // The live sheet's heading is 20px; the in-flow mobile title is 18px. Redeclared as
      // the variable `title` already reads, so the heading never carries two font sizes.
      "max-[767px]:[--lf-title-size:20px] max-[767px]:[--lf-title-leading:1.6]",
    ),
    /**
     * Size and leading arrive as variables, declared by whichever shell is drawing this —
     * `card` for the 392px inline card, `popupShell` for the sheet, `headerOnAside` for
     * the heading that sits on the phone's hero strip. One `text-*` and one `leading-*`
     * utility on the element, so nothing here depends on the order Tailwind emits them.
     */
    title: cn(
      "font-poppins font-medium tracking-tight text-[var(--lf-title)]",
      "text-left",
      "text-[length:var(--lf-title-size,18px)] leading-[var(--lf-title-leading,1.5)]",
    ),
    subtitle: cn(
      "font-poppins font-normal text-left text-[var(--lf-subtitle)]",
      "text-[14px] leading-normal min-[768px]:text-[16px]",
    ),
    body: "grid grid-cols-12 gap-x-[13px] gap-y-[13px]",
    label: cn(
      "mb-1 block text-left font-poppins text-[11px] font-medium leading-tight",
      "text-[var(--lf-label)]",
    ),
    requiredMark: "ml-0.5 text-red-600",
    field: FIELD_BASE,
    // The widget signals an invalid field by FILLING it, not by outlining it — the fields
    // are borderless, so a red border alone reads as a stray box.
    // Redeclares the variables FIELD_BASE reads, rather than stacking a second bg/border.
    fieldInvalid: "[--lf-field-stroke:#F74958] [--lf-field-fill:#FFF3F4]",
    errorText:
      "mt-1 font-poppins text-[10px] font-medium leading-normal tracking-[-0.053px] text-[#F74958]",
    helpText: "mt-1 font-poppins text-[10px] font-normal leading-normal text-[#6B6B6B]",
    // `mt-auto` pins the CTA to the bottom of the 565px row so it lands in the same place
    // on every step, however tall that step's body is.
    actions: "mt-auto flex w-full shrink-0 flex-col gap-[14px] pt-6 min-[768px]:gap-[11px]",
    button: cn(
      "flex w-full items-center justify-center gap-2 rounded-[4px] border-0",
      "font-poppins text-[16px] font-semibold leading-[24px]",
      "h-[48px] px-[18px]",
      "bg-[var(--lf-cta-bg,var(--lf-primary))] text-[var(--lf-on-primary)]",
      "shadow-[0_20px_40px_rgba(9,30,66,0.12)] min-[768px]:shadow-[0_4px_14px_rgba(0,0,0,0.12)]",
      "transition-[box-shadow,color,background-color] disabled:shadow-none",
      "[-webkit-tap-highlight-color:transparent]",
    ),
    // Variant 8 has an explicit disabled FILL (`get_otp_disabled_color`) rather than a
    // faded version of the enabled one, so this repaints instead of dimming.
    buttonDisabled: "cursor-not-allowed [--lf-cta-bg:var(--lf-primary-disabled)] shadow-none",
    secondaryButton: cn(
      "flex h-[48px] w-full items-center justify-center rounded-[4px]",
      "border border-black/10 bg-white px-[18px]",
      "font-poppins text-[14px] font-medium leading-[1.4] text-[var(--lf-field-text)]",
      "transition-colors hover:bg-[#FAFAFA] disabled:opacity-50",
    ),
    consent: cn(
      "font-poppins font-normal text-left text-[var(--lf-consent)]",
      "text-[10px] leading-normal min-[768px]:text-[11px]",
    ),
    consentLink: "underline",

    /* ── OTP gate ────────────────────────────────────────────────────────────────
     * Same two-sentences-around-a-row-of-boxes structure as skin 3000, restyled: the
     * boxes are borderless and shadowed to match the fields rather than outlined. */
    otpBlock: "flex flex-col gap-4",
    otpDescription: cn(
      "font-poppins text-[14px] font-normal leading-[138%] text-left text-[var(--lf-otp-desc)]",
    ),
    otpInlineAction: "font-semibold underline cursor-pointer disabled:cursor-default",
    otpHeader: cn(
      "font-poppins font-semibold leading-[1.5] text-[var(--lf-otp-header)]",
      "text-[16px] min-[768px]:text-[18px]",
    ),
    otpBoxes: "flex items-center gap-[10px]",
    otpBox: cn(
      "size-[44px] rounded-md border text-center shadow-sm",
      "border-[var(--lf-field-stroke,transparent)] bg-[var(--lf-field-fill,var(--lf-field-bg))]",
      "font-poppins text-[16px] font-semibold text-[var(--lf-field-text)]",
      "outline-none ring-0 focus:ring-0 focus-visible:outline-none",
      "disabled:opacity-60",
      "min-[768px]:size-[48px]",
    ),
    otpBoxInvalid: "[--lf-field-stroke:#F74958] [--lf-field-fill:#FFF3F4]",
    otpResend: cn(
      "font-poppins text-[14px] font-normal leading-[138%] text-left text-[#888888]",
    ),

    banner: cn(
      "font-poppins rounded-md border border-[#F74958]/30 bg-[#FFF3F4] px-3 py-2.5",
      "text-[12px] leading-[1.5] text-[#B3202C]",
    ),

    /* ── Terminal screen ─────────────────────────────────────────────────────────
     * Variant 8's thank-you is NOT the form card with different words in it, which is what
     * a first pass at this assumed. It is a distinct, centred screen built around one
     * element — an 80px green disc with a white tick — under which sit a two-line headline,
     * a sentence, and a FILLED CTA carrying a diagonal arrow. All three outcomes (new,
     * returning, brochure) use that same screen; only the words change.
     *
     * Three things follow from that, and each was wrong before:
     *   · the badge is the identity of the screen, so it is drawn (see `renderBackdrop`)
     *     rather than left to an optional author-uploaded illustration
     *   · the corners go back to a plain `rounded-xl` on desktop — the asymmetric pair
     *     belongs to the mobile sheet, not to this card
     *   · the CTA is filled with white text, where the form card's is the brand fill and
     *     this skin's earlier ending was an outlined pill inherited from skin 3000 */
    successCard: cn(
      // `overflow-x-hidden`, not the shorthand: `successCardPopup` adds `overflow-y-auto`
      // and the two land on the SAME element, where a shorthand and a longhand for one
      // axis resolve by stylesheet order.
      "relative mx-auto flex flex-col items-center overflow-x-hidden text-center",
      /**
       * The fill follows the SURFACE, not one card colour.
       *
       * Inline, the ending replaces the lavender form card and must match it — a white
       * block appearing where a lavender one was reads as a different component. The popup
       * ending is white, as `LeadDrop8ThankYou` draws it. Routed through a variable so the
       * popup can override it without a second `bg-*` on the same element.
       */
      "bg-[var(--lf-ending-bg,var(--lf-embed-bg))]",
      // gap-10 between the badge, the words and the CTA — variant 8's wrapper spacing.
      "gap-10",
      // Variant 8's thank-you: a 420px floor and a uniform p-6, not the form's asymmetric
      // padding. The sheet corners stay only on the mobile sheet.
      "min-h-[420px] p-6",
      SHEET_CORNERS,
      "min-[768px]:rounded-xl",
      "min-[1080px]:rounded-[20px]",
      // Sized through VARIABLES, so the popup can widen the card without adding a second
      // `w-*`/`max-w-*` utility beside these — two of either on one element resolve by CSS
      // source order rather than by the order written here.
      // Embedded defaults match the inline card: full width of the host, capped at 392px.
      "w-[var(--lf-ending-w,100%)] max-w-[var(--lf-ending-max,392px)]",
    ),
    /**
     * The popup ending keeps the FORM's width ladder — 320 on the sheet, 620 in the card.
     * A dialog that changes size on its last screen reads as a different dialog opening
     * rather than as this one finishing.
     */
    /**
     * Inside the sheet, beside the hero rail. Layout only — the shell owns the card, the
     * width and the scroll, so this contributes the centring and the 40px rhythm that
     * `successCard` would otherwise carry.
     */
    /**
     * `LeadDrop8ThankYou`'s embedded wrapper, verbatim: `flex w-full flex-col gap-10 py-4`
     * plus the centring its container applies. No card, no background, no min-height, no
     * corners — the shell already draws all of those on both surfaces.
     */
    successContentBare: cn(
      "flex w-full flex-1 flex-col items-center gap-10 py-4 text-center",
      /**
       * TOP-ANCHORED, never centred.
       *
       * `justify-center` was here, and it silently defeated the badge's negative margin:
       * with `flex-1`, centring re-distributes the free space, so pulling the first child up
       * only made the block shorter and the extra space was handed straight back. The badge
       * never moved.
       *
       * Top-anchoring is also what the design wants — variant 8's mobile ending sits at the
       * top of the sheet with the slack below the CTA, not floating in the middle.
       */
      "justify-start",
      /**
       * At 1080+ the sheet is a FIXED 565px row beside the hero rail, so there IS spare
       * height — and centring the whole block left the tick low and the CTA floating in
       * the middle. `justify-start` pulls the tick and the words up; the CTA is pushed to
       * the bottom by `successContent` below.
       */
      "min-[1080px]:pt-6 min-[1080px]:pb-8",
    ),
    // Variant 8 sets the seconds in bold inside the sentence.
    successCountdown: "font-poppins text-center text-sm text-[#222] [&_b]:font-semibold",
    successCardPopup: cn(
      "[--lf-ending-w:calc(100vw-2rem)] [--lf-ending-max:320px]",
      "min-[768px]:[--lf-ending-max:620px]",
      // White in the dialog — the popup ending is its own card, not the inline one.
      "[--lf-ending-bg:var(--lf-card-bg)]",
      "shrink-0 max-h-[90dvh] overflow-y-auto",
    ),
    // Only rendered when an author uploads one; variant 8's own desktop ending has none.
    // `mt-2` so the illustration is not flush against the sheet's top edge on mobile, where
    // the card has no padding of its own above it.
    successImage: "relative z-[1] mt-2 h-[180px] w-[220px] shrink-0 min-[768px]:mt-0",
    // `flex-1` + `justify-between` at 1080+: the words stay under the tick and the CTA is
    // driven to the bottom of the 565px row rather than sitting directly beneath the text.
    successContent: cn(
      /**
       * 30px between the headline and the sentence under it — the same rhythm the headline
       * block uses internally, so the two text levels read as one paragraph rather than as
       * two separate blocks. `gap-10` (40px) pushed the sentence far enough from the title
       * that it looked like a third element.
       */
      "z-[1] flex w-full flex-col items-center gap-[1.875rem]",
      // Fills the space under the badge so the auto margins below have something to divide.
      "min-[1080px]:flex-1",
    ),
    // 30px between the headline and the sentence, capped so lines stay readable.
    /**
     * `mt-auto` here and on `successButton` is what produces the three-band layout: badge
     * near the top, words in the middle, CTA on the floor.
     *
     * Two auto margins rather than `justify-between`: with three children that would put the
     * TITLE directly under the badge and centre the description, which is not the design.
     * An auto margin absorbs free space before `justify-*` is consulted, so two of them
     * divide the slack evenly — one gap above the words, one above the button.
     */
    successHeadings: cn(
      "flex max-w-[22rem] flex-col items-center gap-[1.875rem] min-[768px]:max-w-md",
      "min-[1080px]:mt-auto",
    ),
    successTitle: cn(
      "font-poppins font-semibold leading-normal",
      "text-[18px] min-[768px]:text-xl",
    ),
    // Variant 8's headline is two lines: the sentence, then the programme in the accent
    // colour. The subtitle slot is that second line, which is why it repeats the title's
    // weight and size instead of being smaller.
    successSubtitle: cn(
      "font-poppins font-semibold leading-snug",
      "text-[18px] min-[768px]:text-xl",
    ),
    successBody: "font-poppins text-base font-normal leading-6",
    successButton: cn(
      "flex w-full max-w-[22rem] cursor-pointer items-center justify-center min-[768px]:max-w-md",
      // The CTA sits a little further from the words than the words sit from each other,
      // so it reads as an action rather than as another line of the paragraph.
      "mt-[10px]",
      // See successHeadings: the second auto margin, which drops the CTA to the bottom of
      // the 565px row at desktop. It replaces the margin above at that width.
      "min-[1080px]:mt-auto",
      /**
       * `gap-2 px-4`, down from `gap-[25px] px-6`. The button is `w-full` inside a card
       * that is only 392px wide, so 25px between the label and the arrow plus 48px of
       * padding took 91px out of a 314px button and wrapped "Continue with Your
       * Application" onto two lines on all three endings. The arrow also reads as part of
       * the label at 8px rather than floating away from it.
       */
      "h-12 gap-2 rounded-[4px] px-4",
      // Size comes from the shell, for the same reason the title's does — see `card`.
      "font-poppins text-[length:var(--lf-ending-cta-size,16px)] font-medium leading-[24px]",
      // Filled, white label — the outcome's colour arrives as `--lf-success-cta`.
      "bg-[var(--lf-success-cta)] text-white",
      "shadow-[0px_20px_40px_0px_rgba(9,30,66,0.12)] transition-opacity hover:opacity-90",
    ),

    stepMeta: "text-left font-poppins text-[11px] font-medium leading-tight text-[#8A8A8A]",
    radioLabel: "flex items-center gap-2 font-poppins text-[13px] text-[var(--lf-field-text)]",
  },
};

export default skin3001;
